import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PermissionTypes } from 'constants/enums';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import {
  selectSubscriptionAccountList,
} from 'domains/subscription/selectors';
import {
  commandIamSubscriptionUserList,
  commandIamTeamList,
} from 'containers/ConnectionManager/actions';

import {
  MaterialIcon,
  AutoCompleteSingleInput,
  Avatar,
} from '@pearsontechnology/aero-components';

import collaboratorService from 'services/CollaboratorService';

import { AccountRow, TeamRow } from 'components/AutoCompleteSuggestionItems';

import { selectCurrentMe } from 'data/CurrentUser/selectors';
import { selectTeamList } from 'Workspace/Admin/store/selectors';
import { collaboratorSummaryShape } from 'Workspace/types/collaborator';
import { accountShape } from 'Workspace/types/account';
import { teamShape } from 'Workspace/types/team';
import ActionsDropdown from './components/ActionsDropdown';

import {
  List,
  ListBody,
  ListItem,
  ListColumn,
  InvitePageWrapper,
  TeamIcon,
} from './styles';
import messages from './messages';
import {
  TEAM_TYPE,
  ACCOUNT_TYPE,
} from './constants';

// filterFn exported for testing proposes
export const filterFn = (data, value) => {
  const input = String(value).toLowerCase();
  return data.filter((item) => item.text.toLowerCase().indexOf(input) >= 0);
};

const getAccountFullName = (account) => `${account.givenName || ''} ${account.familyName || ''}`.trim();

class InviteView extends React.Component {
  constructor(props){
    super(props);
    this.handleDelete = this.handleDelete.bind(this);
  }
  static getDerivedStateFromProps(props, state) {
    const { collaborators } = props;

    if (state.collaboratorsByIds) {
      return null;
    }

    return {
      collaboratorsByIds: collaboratorService.getCollaboratorIdsMap(collaborators),
    };
  }

  static normalizeItem(id, type, text, item) {
    return {
      id,
      text,
      type,
      data: item,
    };
  }

  state = {
    collaboratorsList: this.normalizeCollaborators(),
    collaboratorsByIds: null,
  };

  componentDidMount() {
    this.props.getSubscriptionAccountList();
    this.props.getTeamsList();
  }

  componentDidUpdate(prevProps) {
    const { collaborators } = this.props;
  
    const collaboratorsChanged = prevProps.collaborators !== collaborators;
    const accountsChanged = prevProps.collaborators.accounts !== collaborators.accounts;
  
    if (collaboratorsChanged) {
      this.setState({
        collaboratorsByIds: collaboratorService.getCollaboratorIdsMap(collaborators),
      });
    }
  
    if (accountsChanged) {
      this.setState({
        collaboratorsList: this.normalizeCollaborators(),
      });
    }
  }


  onSelect = (selectedItem) => {
    const { id, type } = selectedItem;
    const {
      permissionGrantCommand,
      refId,
      permissionGrant,
      hasStorageInPermissionGrant,
      grantPermissionNoStoreCallback,
    } = this.props;

    if (hasStorageInPermissionGrant) {
      permissionGrant(permissionGrantCommand, id, type, refId, PermissionTypes.CONTRIBUTOR);
    } else {
      grantPermissionNoStoreCallback(id, type, refId, PermissionTypes.CONTRIBUTOR);
    }
  };

  onPermissionChange = (item, refId) => (e) => {
    const {
      permissionGrantCommand,
      permissionGrant,
      hasStorageInPermissionGrant,
      grantPermissionNoStoreCallback,
    } = this.props;
    if (hasStorageInPermissionGrant) {
      permissionGrant(permissionGrantCommand, item.id, item.type, refId, e.permission);
    } else {
      grantPermissionNoStoreCallback(item.id, item.type, refId, e.permission);
    }
  };

  getCollaboratorsListItem = (item, readOnly, handleDelete, refId, currentUserPermission) => (
    <ListItem key={`sharing-dropdown-list-${item.id}`}>
      <ListColumn colWidth="0%" colWidthMobile="0%" grow="0">
        {
          item.type === TEAM_TYPE ?
            <TeamIcon>
              <MaterialIcon id="people" />
            </TeamIcon> :
            <Avatar avatar={item.data.account} size={30} />
        }
      </ListColumn>
      <ListColumn colWidth="0%" colWidthMobile="0%" style={{ paddingLeft: 6 }}>
        {item.text}
      </ListColumn>
      <ListColumn colWidthMobile="0%" grow="0">
        {
          !readOnly && <ActionsDropdown
            currentUserPermission={currentUserPermission}
            currentUser={this.props.currentUser}
            collaborator={item.data}
            onDelete={()=>{handleDelete(item);}}
            onChangePermission={this.onPermissionChange(item, refId)}
          />
        }
      </ListColumn>
    </ListItem>
  );

  getCurrentUserPermissionLevel() {
    const {
      collaborators,
      currentUser,
      teamsList,
    } = this.props;
    const { collaboratorsByIds } = this.state;

    const collaborator = collaboratorService.getCollaboratorByUserId(collaborators, currentUser.accountId);
    if (!collaborator) {
      const teamsIds = Object.keys(collaboratorsByIds.teams);
      const teams = teamsList.filter((team) => teamsIds.includes(team.teamId));
      const collaboratorTeam = collaboratorService.getCollaboratorTeamByAccountId(collaborators, teams, currentUser.accountId);

      return collaboratorTeam && collaboratorTeam.permissionLevel;
    }

    return collaborator && collaborator.permissionLevel;
  }

  normalizeTeamsAndAccounts() {
    const { teamsList, subscriptionAccountList } = this.props;
    const _teamsList = teamsList || [];
    const _subscriptionAccountList = subscriptionAccountList || [];

    return [
      ..._teamsList.map((item) => InviteView.normalizeItem(item.teamId, TEAM_TYPE, item.name, item)),
      ..._subscriptionAccountList.map((item) => InviteView.normalizeItem(item.accountId, ACCOUNT_TYPE, getAccountFullName(item), item)),
    ];
  }

  normalizeCollaborators() {
    const { teams = [], accounts = [] } = this.props.collaborators;

    return [
      ...teams.map((item) => InviteView.normalizeItem(item.team.id, TEAM_TYPE, item.team.name, item)),
      ...accounts.map((item) => InviteView.normalizeItem(item.account.accountId, ACCOUNT_TYPE, getAccountFullName(item.account), item)),
    ];
  }

  handleDelete(userId){
    if (this.props.handleDelete) {
      this.props.handleDelete(userId);
    }
  }

  render() {
    const {
      readOnly,
      refId,
      subscriptionAccountList,
      teamsList,
    } = this.props;

    const {
      collaboratorsList,
      collaboratorsByIds,
    } = this.state;

    if (!(subscriptionAccountList && teamsList)) {
      return null;
    }
    const currentUserPermissionLevel = this.getCurrentUserPermissionLevel();
    const autoCompleteList = this
      .normalizeTeamsAndAccounts()
      .filter((collaborator) => !collaboratorsByIds[`${collaborator.type}s`][collaborator.id]);
    collaboratorsList.sort((a, b) => {
      if (a.type === "team" && b.type !== "team") {
        return -1;
      }
      if (a.type !== "team" && b.type === "team") {
        return 1;
      }
      return a.text.localeCompare(b.text);
    });
    return (
      <InvitePageWrapper>
        <AutoCompleteSingleInput
          filterFunction={filterFn}
          renderSuggestionItem={(data) => {
            if (data.type === TEAM_TYPE) {
              return <TeamRow data={data} />;
            }

            if (data.type === ACCOUNT_TYPE) {
              return <AccountRow data={data} />;
            }

            return null;
          }}
          data={autoCompleteList}
          onSelect={this.onSelect}
          noResultsText={intl.formatMessage(messages.noResults)}
          label={intl.formatMessage(messages.invitePeople)}
          placeholder={intl.formatMessage(messages.inputPlaceholder)}
        />
        <List>
          <ListBody>
            {
              collaboratorsList.map((item) =>
                this.getCollaboratorsListItem(
                  item,
                  readOnly,
                  this.handleDelete,
                  refId,
                  currentUserPermissionLevel
                )
              )
            }
          </ListBody>
        </List>

      </InvitePageWrapper>
    );
  }
}

InviteView.propTypes = {
  readOnly: PropTypes.bool,
  collaborators: collaboratorSummaryShape,
  handleDelete: PropTypes.func,
  subscriptionAccountList: PropTypes.arrayOf(accountShape),
  teamsList: PropTypes.arrayOf(teamShape),
  getSubscriptionAccountList: PropTypes.func,
  getTeamsList: PropTypes.func,
  permissionGrantCommand: PropTypes.func,
  permissionGrant: PropTypes.func,
  hasStorageInPermissionGrant: PropTypes.bool,
  grantPermissionNoStoreCallback: PropTypes.func,
  refId: PropTypes.string,
  currentUser: PropTypes.object,
};

const mapStateToProps = (state) => ({
  subscriptionAccountList: selectSubscriptionAccountList(state),
  teamsList: selectTeamList(state),
  currentUser: selectCurrentMe(state),
});

const mapDispatchToProps = (dispatch) => ({
  getSubscriptionAccountList: () => dispatch(commandIamSubscriptionUserList()),
  getTeamsList: () => dispatch(commandIamTeamList()),
  permissionGrant: (command, id, type, refId, permissionLevel) => (
    dispatch(command(id, type, refId, permissionLevel))
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(InviteView);
